<template>
  <div class="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">

    <!-- Card Header -->
    <div class="px-6 py-4 border-b border-slate-100 bg-gradient-to-r from-orange-500 to-orange-700
                flex items-center justify-between flex-wrap gap-3">
      <div class="flex items-center gap-3">
        <svg class="w-5 h-5 text-white/80" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
            d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"/>
        </svg>
        <span class="text-white font-semibold text-sm">All Reviews</span>
        <span class="bg-white/20 text-white text-xs font-bold px-2.5 py-0.5 rounded-full">
          {{ reviews.total ?? 0 }}
        </span>
      </div>

      <!-- Filters -->
      <div class="flex items-center gap-2 flex-wrap">
        <InputText
          v-model="search"
          @input="getReviews(null)"
          placeholder="Search…"
          class="!text-sm !py-1.5 !px-3 !rounded-lg !border-white/30 !bg-white/15 !text-white placeholder:!text-white/60 !w-36"
        />
        <Select
          v-model="status"
          :options="statusOptions"
          optionLabel="label"
          optionValue="value"
          placeholder="Status"
          @change="getReviews(null)"
          class="hdr-select !text-sm !rounded-lg !bg-white/15 !border-white/30 !text-white !w-32"
        />
        <Select
          v-model="perPage"
          :options="perPageOptions"
          optionLabel="label"
          optionValue="value"
          @change="getReviews(null)"
          class="hdr-select !text-sm !rounded-lg !bg-white/15 !border-white/30 !text-white !w-20"
        />
        <button @click="getReviews(null)"
                class="p-1.5 bg-white/15 hover:bg-white/25 rounded-lg transition-colors" title="Refresh">
          <svg class="w-4 h-4 text-white" :class="{ 'animate-spin': reviewloading }" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99"/>
          </svg>
        </button>
      </div>
    </div>

    <!-- Rating filter row -->
    <div class="px-6 py-3 bg-slate-50 border-b border-slate-100 flex items-center gap-3 flex-wrap">
      <span class="text-xs font-semibold text-slate-500 uppercase tracking-wider">Filter by Rating</span>
      <div class="flex gap-1">
        <button v-for="star in [null,1,2,3,4,5]" :key="star"
                @click="rate = star; getReviews(null)"
                class="px-2.5 py-1 text-xs font-semibold rounded-lg border transition-all duration-150"
                :class="rate === star
                  ? 'bg-orange-600 text-white border-orange-600 shadow-sm'
                  : 'bg-white text-slate-500 border-slate-200 hover:border-orange-300 hover:text-orange-600'">
          {{ star === null ? 'All' : '★'.repeat(star) }}
        </button>
      </div>
      <span class="ml-auto text-xs text-slate-400">{{ reviews.total ?? 0 }} results</span>
    </div>

    <!-- Table -->
    <div class="overflow-x-auto">
      <table class="w-full">
        <thead>
          <tr class="bg-slate-50 border-b border-slate-100">
            <th class="px-5 py-3.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">ID</th>
            <th class="px-5 py-3.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Customer</th>
            <th class="px-5 py-3.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Rating</th>
            <th class="px-5 py-3.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Title</th>
            <th class="px-5 py-3.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Description</th>
            <th class="px-5 py-3.5 text-center text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
            <th class="px-5 py-3.5 text-center text-xs font-semibold text-slate-500 uppercase tracking-wider">Actions</th>
          </tr>
        </thead>

        <!-- Skeleton loading -->
        <tbody v-if="reviewloading">
          <tr v-for="i in 5" :key="i" class="border-b border-slate-50">
            <td class="px-5 py-4"><div class="h-4 w-8 bg-slate-200 rounded-lg animate-pulse"></div></td>
            <td class="px-5 py-4"><div class="h-4 w-32 bg-slate-200 rounded-lg animate-pulse"></div></td>
            <td class="px-5 py-4"><div class="h-4 w-20 bg-slate-200 rounded-lg animate-pulse"></div></td>
            <td class="px-5 py-4"><div class="h-4 w-36 bg-slate-200 rounded-lg animate-pulse"></div></td>
            <td class="px-5 py-4"><div class="h-4 w-48 bg-slate-200 rounded-lg animate-pulse"></div></td>
            <td class="px-5 py-4 text-center"><div class="h-6 w-16 bg-slate-200 rounded-full animate-pulse mx-auto"></div></td>
            <td class="px-5 py-4 text-center"><div class="h-8 w-24 bg-slate-200 rounded-xl animate-pulse mx-auto"></div></td>
          </tr>
        </tbody>

        <!-- Empty state -->
        <tbody v-else-if="!reviews.data || reviews.data.length === 0">
          <tr>
            <td colspan="7" class="px-5 py-20">
              <div class="text-center">
                <div class="w-16 h-16 bg-orange-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <svg class="w-8 h-8 text-orange-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
                      d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"/>
                  </svg>
                </div>
                <p class="font-semibold text-slate-600">No reviews yet</p>
                <p class="text-sm text-slate-400 mt-1">When customers leave reviews, they'll appear here.</p>
              </div>
            </td>
          </tr>
        </tbody>

        <!-- Data rows -->
        <tbody v-else class="divide-y divide-slate-50">
          <tr v-for="review in reviews.data" :key="review.id"
              class="hover:bg-orange-50/40 transition-colors duration-150 group">

            <!-- ID -->
            <td class="px-5 py-4">
              <span class="font-mono text-xs font-bold text-orange-700 bg-orange-50 px-2 py-1 rounded-lg tracking-wider">
                #{{ review.id }}
              </span>
            </td>

            <!-- Customer -->
            <td class="px-5 py-4">
              <div class="flex items-center gap-2.5">
                <div class="w-8 h-8 rounded-full bg-gradient-to-br from-orange-400 to-amber-500 flex items-center
                            justify-center text-white text-xs font-bold flex-shrink-0">
                  {{ review.full_name?.slice(0,2).toUpperCase() }}
                </div>
                <span class="text-sm font-semibold text-slate-800 max-w-[160px] truncate">{{ review.full_name }}</span>
              </div>
            </td>

            <!-- Rating -->
            <td class="px-5 py-4">
              <div class="flex items-center gap-1">
                <span v-for="s in 5" :key="s" class="text-sm" :class="s <= review.rate ? 'text-orange-500' : 'text-slate-200'">★</span>
                <span class="ms-1 text-xs font-semibold text-slate-500">{{ review.rate }}/5</span>
              </div>
            </td>

            <!-- Title -->
            <td class="px-5 py-4">
              <span class="text-sm text-slate-700 font-medium max-w-[180px] truncate block">{{ review.title }}</span>
            </td>

            <!-- Description -->
            <td class="px-5 py-4">
              <span class="text-sm text-slate-500 max-w-[260px] truncate block">{{ review.description }}</span>
            </td>

            <!-- Active toggle -->
            <td class="px-5 py-4 text-center">
              <button @click="toggleActive(review)"
                      class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold transition-all duration-200"
                      :class="review.active
                        ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
                        : 'bg-red-100 text-red-600 hover:bg-red-200'">
                <span class="w-1.5 h-1.5 rounded-full" :class="review.active ? 'bg-emerald-500' : 'bg-red-500'"></span>
                {{ review.active ? 'Active' : 'Inactive' }}
              </button>
            </td>

            <!-- Actions -->
            <td class="px-5 py-4 text-center">
              <div class="flex items-center justify-center gap-1">
                <RouterLink :to="{ name: 'app.reviews.edit', params: { id: review.id } }"
                            class="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold
                                   bg-orange-50 text-orange-700 border border-orange-100
                                   hover:bg-orange-100 hover:border-orange-200
                                   rounded-xl transition-all duration-200 group-hover:shadow-sm"
                            title="Edit">
                  <PencilIcon class="w-3.5 h-3.5"/>
                  <span class="hidden sm:inline">Edit</span>
                </RouterLink>
                <RouterLink :to="{ name: 'app.reviews.show', params: { id: review.id } }"
                            class="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold
                                   bg-slate-50 text-slate-700 border border-slate-200
                                   hover:bg-slate-100 hover:border-slate-300
                                   rounded-xl transition-all duration-200"
                            title="View">
                  <EyeIcon class="w-3.5 h-3.5"/>
                </RouterLink>
                <button @click="deleteReview(review.id)"
                        class="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold
                               bg-red-50 text-red-600 border border-red-100
                               hover:bg-red-100 hover:border-red-200
                               rounded-xl transition-all duration-200"
                        title="Delete">
                  <TrashIcon class="w-3.5 h-3.5"/>
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Pagination -->
    <div v-if="!reviewloading && reviews.total > 0"
         class="px-6 py-4 border-t border-slate-100 flex flex-col md:flex-row justify-between items-center gap-3">
      <span class="text-sm text-slate-500">
        Showing
        <span class="font-semibold text-slate-700">{{ reviews.from }}</span>
        –
        <span class="font-semibold text-slate-700">{{ reviews.to }}</span>
        of
        <span class="font-semibold text-orange-600">{{ reviews.total }}</span>
        reviews
      </span>
      <nav v-if="reviews.total > reviews.limit"
           class="inline-flex rounded-xl shadow-sm -space-x-px overflow-hidden border border-slate-200"
           aria-label="Pagination">
        <a v-for="(link, i) of reviews.links" :key="i"
           :disabled="!link.url"
           href="#"
           @click.prevent="getForPage($event, link)"
           class="relative inline-flex items-center px-3.5 py-2 border-e border-slate-200 text-sm font-medium transition-all duration-150 whitespace-nowrap"
           :class="[
             link.active ? 'bg-orange-600 text-white z-10' : 'bg-white text-slate-600 hover:bg-orange-50 hover:text-orange-600',
             !link.url ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer',
           ]"
           v-html="link.label">
        </a>
      </nav>
    </div>
  </div>
</template>

<script setup>

import { ref, computed, onMounted } from 'vue';
import store from "@/store";
import { PencilIcon, TrashIcon, EyeIcon } from '@heroicons/vue/24/outline';
import { RouterLink } from "vue-router";
import InputText from "primevue/inputtext";
import { Select } from "primevue";
import { useToast } from "primevue/usetoast";

const toast = useToast();
const perPage = ref(10);
const rate = ref(null);
const perPageOptions = [
  { label: '10', value: 10 },
  { label: '20', value: 20 },
  { label: '30', value: 30 },
];
const statusOptions = [
  { label: 'All', value: null },
  { label: 'Active', value: true },
  { label: 'Not Active', value: false },
];
const status = ref(null);
const search = ref('');

const reviews = computed(() => store.state.reviews);
const reviewloading = ref(true);

const showSuccess = (detail) => {
  toast.add({ severity: "success", summary: "Success", detail, life: 3000 });
};

function toggleActive(review) {
  store.dispatch('toggleReviewActive', review.id)
    .then(() => {
      review.active = !review.active;
      showSuccess('Review status updated successfully');
    })
    .catch(err => console.log(err));
}

function getReviews(url = null) {
  reviewloading.value = true;
  store.dispatch('getReviews', {
    url,
    rate: rate.value,
    search: search.value,
    perPage: perPage.value,
    status: status.value,
  }).then(() => { reviewloading.value = false; });
}

function getForPage(ev, link) {
  if (!link.url || link.active) return;
  getReviews(link.url);
}

function deleteReview(id) {
  if (!confirm('Are you sure you want to delete this review?')) return;
  store.dispatch('deleteReview', id)
    .then(() => {
      showSuccess('Review deleted successfully');
      getReviews();
    });
}

onMounted(() => { getReviews(); });
</script>

<style scoped>
/* PrimeVue Select paints its own label/icon colors, so the utility classes on
   the root element never reach them — force white for the header filters. */
.hdr-select :deep(.p-select-label) {
  color: #fff;
}

.hdr-select :deep(.p-select-label.p-placeholder) {
  color: rgba(255, 255, 255, 0.6);
}

.hdr-select :deep(.p-select-dropdown-icon) {
  color: rgba(255, 255, 255, 0.8);
}
</style>
